import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import api from '../../Includes/api/api'
import { loadGoogleMaps } from '../../Includes/googleMaps'
import { useSignupAdmin } from '../../Includes/signupAdmin'
import { Calendar, User, Info, MapPin, List, Table, FileText, CheckCircle, Clock, Search, X, Building2, TrendingUp, Phone, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react'

// Returns only the school/college name (text before first comma)
const schoolName = (areaName) => (areaName || '').split(',')[0].trim()

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[ch]))

const kmBetween = (a, b) => {
  const r = 6371
  const dLat = (b.lat - a.lat) * Math.PI / 180
  const dLng = (b.lng - a.lng) * Math.PI / 180
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return 2 * r * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

const finitePoint = (lat, lng) => {
  const latitude = Number(lat)
  const longitude = Number(lng)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  return { lat: latitude, lng: longitude }
}

const visitMarkerIcon = (maps, visit) => {
  const interest = visit.interest_status
  const color = interest === 'interested' ? '#10b981' : interest === 'not_interested' ? '#ef4444' : '#64748b'
  const size = visit.is_last ? 36 : visit.is_first ? 22 : 18
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" stroke="white" stroke-width="2"/><text x="${size / 2}" y="${size / 2 + 4}" text-anchor="middle" fill="white" font-size="11" font-family="Arial" font-weight="700">${visit.sequence}</text></svg>`
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new maps.Size(size, size),
    anchor: new maps.Point(size / 2, visit.is_last ? size : size / 2),
  }
}

function GoogleVisitsMap({ visits, paths, center, onCompany }) {
  const nodeRef = useRef(null)
  const onCompanyRef = useRef(onCompany)
  useEffect(() => { onCompanyRef.current = onCompany }, [onCompany])
  useEffect(() => {
    let cancelled = false
    loadGoogleMaps().then((maps) => {
      if (cancelled || !nodeRef.current) return
      const map = new maps.Map(nodeRef.current, {
        center: { lat: Number(center[0]), lng: Number(center[1]) },
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
      })
      const info = new maps.InfoWindow()
      const bounds = new maps.LatLngBounds()
      paths.forEach((path) => {
        new maps.Polyline({
          map,
          path: path.positions.map(([lat, lng]) => ({ lat, lng })),
          strokeColor: path.color,
          strokeWeight: 3,
          strokeOpacity: 0.8,
        })
      })
      visits.forEach((visit) => {
        const school = finitePoint(visit.adjusted_lat, visit.adjusted_lng)
        const ended = finitePoint(visit.end_latitude, visit.end_longitude)
        const when = visit.visited_date || visit.planned_date || visit.created_at
        const interestLabel = visit.interest_status === 'interested'
          ? 'Interested'
          : visit.interest_status === 'not_interested' ? 'Not Interested' : 'Pending'
        const summaryButton = `
          <button id="visit-summary-${visit.id}" type="button" style="margin-top:8px">View visit summary</button>
        `
        const openSummary = () => {
          maps.event.addListenerOnce(info, 'domready', () => {
            document.getElementById(`visit-summary-${visit.id}`)?.addEventListener('click', () => {
              onCompanyRef.current?.(schoolName(visit.area_name))
            })
          })
        }
        if (school) {
          bounds.extend(school)
          const marker = new maps.Marker({
            map,
            position: school,
            icon: visitMarkerIcon(maps, visit),
            title: `${schoolName(visit.area_name)} · school`,
            zIndex: 2,
          })
          marker.addListener('click', () => {
            info.setContent(`
              <div style="font-size:13px;line-height:1.5;min-width:180px">
                <strong>${visit.sequence}. ${escapeHtml(schoolName(visit.area_name))}</strong>
                <div style="color:#1d4ed8;font-weight:700">School point</div>
                <div>Visitor: ${escapeHtml(visit.person_detail?.name || '—')}</div>
                ${visit.is_support_visit ? '<div style="color:#7c3aed;font-weight:700">Support visit</div>' : ''}
                <div>Date: ${when ? new Date(when).toLocaleDateString() : '—'}</div>
                <div>Ride started: ${escapeHtml(formatTime(visit.start_time))}</div>
                <div>Arrived: ${escapeHtml(formatTime(visit.school_arrival_time))}</div>
                <div>Visit ended: ${escapeHtml(formatTime(visit.end_time))}</div>
                <div>From previous: ${Number(visit.prev_distance_km || 0).toFixed(2)} km</div>
                <div>${escapeHtml(interestLabel)}</div>
                ${summaryButton}
              </div>
            `)
            info.open(map, marker)
            openSummary()
          })
        }
        if (!ended) return
        const endedNearSchool = school && kmBetween(school, ended) < 0.04
        const endedPosition = endedNearSchool
          ? { lat: ended.lat + 0.00022, lng: ended.lng + 0.00022 }
          : ended
        bounds.extend(endedPosition)
        if (school && !endedNearSchool) {
          new maps.Polyline({
            map,
            path: [school, ended],
            strokeColor: '#f59e0b',
            strokeOpacity: 0,
            strokeWeight: 2,
            icons: [{
              icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3, strokeColor: '#f59e0b' },
              offset: '0',
              repeat: '12px',
            }],
            zIndex: 1,
          })
        }
        const endMarker = new maps.Marker({
          map,
          position: endedPosition,
          title: `${schoolName(visit.area_name)} · visit ended`,
          zIndex: 3,
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png',
          },
        })
        endMarker.addListener('click', () => {
          info.setContent(`
            <div style="font-size:13px;line-height:1.5;min-width:180px">
              <strong>${escapeHtml(schoolName(visit.area_name))}</strong>
              <div style="color:#d97706;font-weight:700">Ended point</div>
              <div>Visitor: ${escapeHtml(visit.person_detail?.name || '—')}</div>
              <div>Visit ended: ${escapeHtml(formatTime(visit.end_time))}</div>
              <div>${escapeHtml(interestLabel)}</div>
              ${summaryButton}
            </div>
          `)
          info.open(map, endMarker)
          openSummary()
        })
      })
      if (!bounds.isEmpty()) map.fitBounds(bounds, 60)
    }).catch(() => {})
    return () => {
      cancelled = true
      if (nodeRef.current) nodeRef.current.innerHTML = ''
    }
  }, [visits, paths, center])
  return <div ref={nodeRef} style={{ height: '100%', width: '100%' }} />
}

const formatTime = (isoString) => {
  if (!isoString) return '—'
  const date = new Date(isoString)
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}

/* ─── Company Detail Tab ─────────────────────────────────────────────────── */
function CompanyDetailTab({ company, allVisits, followups }) {
  const visits = useMemo(() =>
    allVisits.filter(v => schoolName(v.area_name).toLowerCase() === company.toLowerCase()),
    [allVisits, company]
  )

  const followupMap = useMemo(() => {
    const map = {}
    followups.forEach(f => {
      const vid = f.visit_detail?.id || f.visit
      if (vid && !map[vid]) map[vid] = f
    })
    return map
  }, [followups])

  const totalVisits = visits.length

  const statusBadge = (status) => {
    if (status === 'interested') return { bg: '#dcfce7', text: '#16a34a', label: '✓ Interested' }
    if (status === 'not_interested') return { bg: '#fee2e2', text: '#ef4444', label: '✗ Not Interested' }
    return { bg: '#f1f5f9', text: '#64748b', label: '⏳ Pending' }
  }

  if (visits.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '260px', gap: '14px', color: 'var(--text-muted)' }}>
        <Building2 size={44} />
        <strong style={{ fontSize: '15px' }}>No visits found for "{company}"</strong>
        <span style={{ fontSize: '13px' }}>Try adjusting filters or check the company name.</span>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Header banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 60%, #3b82f6 100%)',
        borderRadius: '12px', padding: '24px 28px', color: '#fff',
        display: 'flex', alignItems: 'center', gap: '20px'
      }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '14px',
          background: 'rgba(255,255,255,0.18)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontSize: '28px', flexShrink: 0
        }}>🏫</div>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>{company}</h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.85 }}>
            {totalVisits} visit{totalVisits !== 1 ? 's' : ''} recorded · Bangalore
          </p>
        </div>
      </div>

      {/* Visits table */}
      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TrendingUp size={16} style={{ color: 'var(--accent-blue)' }} />
          <strong style={{ fontSize: '14px', color: 'var(--text-primary)' }}>All Visits to {company}</strong>
        </div>
        <div className="table-container">
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left', background: '#f8fafc' }}>
                {['👤 Visitor', 'Interest Status', '📅 Planned Date', '📅 Visited Date', '📞 Contact', '🔄 Follow-up Date', '📝 Follow-up Purpose'].map(h => (
                  <th key={h} style={{ padding: '11px 16px', fontWeight: 600, fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visits.map(visit => {
                const badge = statusBadge(visit.interest_status)
                const fu = followupMap[visit.id]
                const followupDate = fu?.followup_date || visit.revisit_date
                return (
                  <tr key={visit.id} style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ padding: '13px 16px', fontWeight: 600, fontSize: '13px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '30px', height: '30px', borderRadius: '50%',
                          background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: '12px', fontWeight: 700, flexShrink: 0
                        }}>
                          {(visit.person_detail?.name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <div>{visit.person_detail?.name || '—'}</div>
                          {visit.person_detail?.designation && (
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400 }}>{visit.person_detail.designation}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
                        fontSize: '11px', fontWeight: 700, background: badge.bg, color: badge.text
                      }}>{badge.label}</span>
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {visit.planned_date ? new Date(visit.planned_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {visit.visited_date ? new Date(visit.visited_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px' }}>
                      {visit.contact_number ? (
                        <span style={{ color: '#2563eb', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Phone size={12} /> {visit.contact_number}
                        </span>
                      ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', fontWeight: followupDate ? 600 : 400, color: followupDate ? 'var(--accent-blue)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {followupDate ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Clock size={12} />
                          {new Date(followupDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={fu?.next_purpose || '—'}>
                      {fu?.next_purpose || '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/* ─── Search Box with Autocomplete ──────────────────────────────────────── */
function CompanySearchBox({ visits, onSelect, onClear, selectedCompany }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  // Unique company names
  const companies = useMemo(() => {
    const names = [...new Set(visits.map(v => schoolName(v.area_name)).filter(Boolean))]
    return names.sort()
  }, [visits])

  const filtered = useMemo(() =>
    query.trim().length >= 1
      ? companies.filter(c => c.toLowerCase().includes(query.toLowerCase()))
      : companies.slice(0, 8),
    [query, companies]
  )

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelect = (name) => {
    setQuery(name)
    setOpen(false)
    onSelect(name)
  }

  const handleClear = () => {
    setQuery('')
    setOpen(false)
    onClear()
  }

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: '280px' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Search size={15} style={{ position: 'absolute', left: '10px', color: 'var(--text-muted)', pointerEvents: 'none' }} />
        <input
          type="text"
          value={query}
          placeholder="Search company / school…"
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          style={{
            width: '100%', padding: '7px 34px 7px 32px', fontSize: '13px',
            border: '1.5px solid var(--border-color)', borderRadius: '8px',
            background: 'var(--bg-primary)', color: 'var(--text-primary)',
            outline: 'none', transition: 'border-color 0.2s',
            boxShadow: open ? '0 0 0 3px rgba(37,99,235,0.1)' : 'none',
            borderColor: open ? '#2563eb' : 'var(--border-color)'
          }}
        />
        {query && (
          <button onClick={handleClear} style={{
            position: 'absolute', right: '8px', background: 'none', border: 'none',
            cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: '2px'
          }}>
            <X size={14} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: 'var(--bg-primary)', border: '1.5px solid var(--border-color)',
          borderRadius: '10px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          zIndex: 9999, maxHeight: '280px', overflowY: 'auto'
        }}>
          {filtered.map(name => (
            <div
              key={name}
              onMouseDown={() => handleSelect(name)}
              style={{
                padding: '10px 14px', fontSize: '13px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px',
                background: name === selectedCompany ? '#eff6ff' : 'transparent',
                color: name === selectedCompany ? '#2563eb' : 'var(--text-primary)',
                fontWeight: name === selectedCompany ? 700 : 400,
                borderBottom: '1px solid var(--border-color)',
                transition: 'background 0.12s'
              }}
              onMouseEnter={e => { if (name !== selectedCompany) e.currentTarget.style.background = '#f8fafc' }}
              onMouseLeave={e => { if (name !== selectedCompany) e.currentTarget.style.background = 'transparent' }}
            >
              <span style={{ fontSize: '15px' }}>🏫</span>
              <span>{name}</span>
              {name === selectedCompany && <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#2563eb' }}>● Selected</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
const todayIso = () => {
  const t = new Date()
  const yyyy = t.getFullYear()
  const mm = String(t.getMonth() + 1).padStart(2, '0')
  const dd = String(t.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const parseLocalDate = (value) => {
  const [year, month, day] = String(value || '').split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

const visitRows = (body) => {
  if (Array.isArray(body?.data)) return body.data
  if (Array.isArray(body?.results)) return body.results
  if (Array.isArray(body)) return body
  return []
}

const fetchAllVisitPages = async (params) => {
  const pageSize = params.page_size || 20
  const first = await api.get('/visits/', { params: { ...params, page: 1, page_size: pageSize } })
  const firstRows = visitRows(first.data)
  const count = Number(first.data?.count)
  if (!Number.isFinite(count) || firstRows.length >= count) return firstRows

  const pageCount = Math.ceil(count / pageSize)
  const rest = []
  for (let start = 2; start <= pageCount; start += 5) {
    const batch = await Promise.all(
      Array.from({ length: Math.min(5, pageCount - start + 1) }, (_, index) =>
        api.get('/visits/', { params: { ...params, page: start + index, page_size: pageSize } })
      )
    )
    batch.forEach((res) => rest.push(...visitRows(res.data)))
  }
  return [...firstRows, ...rest]
}

export default function VisitsMap() {
  const [persons, setPersons] = useState([])
  const [selectedPerson, setSelectedPerson] = useState('')
  const [period, setPeriod] = useState('custom')
  const [dateFrom, setDateFrom] = useState(todayIso)
  const [dateTo, setDateTo] = useState(todayIso)
  const [selectedStatus, setSelectedStatus] = useState('visited')
  const [visits, setVisits] = useState([])
  const [followups, setFollowups] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('map') // 'map' | 'summary' | 'company'
  const [previousTab, setPreviousTab] = useState('map')
  const [selectedCompany, setSelectedCompany] = useState('') // for company detail tab
  const [summaryPage, setSummaryPage] = useState(1)
  const [summaryItemsPerPage, setSummaryItemsPerPage] = useState(20)
  const [academicYears, setAcademicYears] = useState([])
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('')

  const isAdmin = useSignupAdmin()

  // Force active tab to map if user is not admin
  useEffect(() => {
    if (!isAdmin && activeTab === 'summary') {
      setActiveTab('map')
    }
  }, [activeTab, isAdmin])

  // Load persons and academic years
  useEffect(() => {
    api.get('/persons/').then(res => {
      const raw = res.data
      setPersons(
        Array.isArray(raw?.data) ? raw.data :
          Array.isArray(raw?.results) ? raw.results :
            Array.isArray(raw) ? raw : []
      )
    }).catch(() => { })

    api.get('/basic/academicyear/').then(res => {
      const raw = res.data
      const years = Array.isArray(raw?.data) ? raw.data :
        Array.isArray(raw?.results) ? raw.results :
          Array.isArray(raw) ? raw : []
      setAcademicYears(years)
      const activeYear = years.find(y => y.is_active)
      if (!years.length) setLoading(false)
      setSelectedAcademicYear(String(activeYear?.id || years[0]?.id || ''))
    }).catch(() => { setLoading(false) })
  }, [])

  // Fetch visits and follow-ups for the selected academic year
  const fetchData = useCallback(async () => {
    if (!selectedAcademicYear) return
    setLoading(true)
    try {
      const params = { page_size: 20, academic_year_id: selectedAcademicYear }
      if (selectedPerson) params.person_id = selectedPerson

      const [salesVisits, supportVisits, followupsRes] = await Promise.all([
        fetchAllVisitPages({ ...params, visit_kind: 'sales' }),
        fetchAllVisitPages({ ...params, visit_kind: 'support' }),
        api.get('/visits/followups/', { params: { academic_year_id: selectedAcademicYear } })
      ])

      const seen = new Set()
      const merged = []
      ;[...salesVisits, ...supportVisits].forEach((visit) => {
        const key = String(visit?.id ?? '')
        if (!key || seen.has(key)) return
        seen.add(key)
        merged.push(visit)
      })
      setVisits(merged)

      const rawFollowups = followupsRes.data
      setFollowups(
        Array.isArray(rawFollowups?.data) ? rawFollowups.data :
          Array.isArray(rawFollowups?.results) ? rawFollowups.results :
            Array.isArray(rawFollowups) ? rawFollowups : []
      )
    } catch (err) {
      console.error("Failed to load data:", err)
    } finally {
      setLoading(false)
    }
  }, [selectedPerson, selectedAcademicYear])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Map each visit to its latest follow-up
  const visitsWithFollowup = useMemo(() => {
    const followupMap = {}
    followups.forEach(f => {
      const visitId = f.visit_detail?.id || f.visit
      if (visitId && !followupMap[visitId]) {
        followupMap[visitId] = f
      }
    })
    return visits.map(v => ({
      ...v,
      latest_followup: followupMap[v.id] || null
    }))
  }, [visits, followups])

  // Filter visits by period & status
  const filteredVisits = useMemo(() => {
    const now = new Date()
    return visitsWithFollowup.filter(visit => {
      if (selectedStatus && visit.status !== selectedStatus) return false
      let dateFields = []
      if (selectedStatus === 'visited') {
        dateFields = [visit.visited_date, visit.start_time, visit.school_arrival_time, visit.end_time]
      } else if (selectedStatus === 'planned') {
        dateFields = [visit.planned_date, visit.created_at]
      } else {
        dateFields = [visit.visited_date, visit.start_time, visit.school_arrival_time, visit.end_time, visit.planned_date, visit.created_at]
      }

      const datesToCheck = dateFields.filter(Boolean).map(d => new Date(d))

      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

      const checkDateMatch = (targetDate) => {
        return datesToCheck.some(d => {
          const check = new Date(d.getFullYear(), d.getMonth(), d.getDate())
          return check.getTime() === targetDate.getTime()
        })
      }

      if (period === 'day') return checkDateMatch(today)
      
      if (period === 'week') {
        return datesToCheck.some(d => {
          const check = new Date(d.getFullYear(), d.getMonth(), d.getDate())
          const diffDays = Math.ceil(Math.abs(today - check) / (1000 * 60 * 60 * 24))
          return diffDays <= 7
        })
      }
      if (period === 'month') {
        return datesToCheck.some(d => {
          const check = new Date(d.getFullYear(), d.getMonth(), d.getDate())
          const diffDays = Math.ceil(Math.abs(today - check) / (1000 * 60 * 60 * 24))
          return diffDays <= 30
        })
      }
      if (period === 'year') {
        return datesToCheck.some(d => {
          const check = new Date(d.getFullYear(), d.getMonth(), d.getDate())
          const diffDays = Math.ceil(Math.abs(today - check) / (1000 * 60 * 60 * 24))
          return diffDays <= 365
        })
      }
      if (period === 'custom' && (dateFrom || dateTo)) {
        const start = parseLocalDate(dateFrom)
        const end = parseLocalDate(dateTo)
        return datesToCheck.some(d => {
          const check = new Date(d.getFullYear(), d.getMonth(), d.getDate())
          if (start && check < start) return false
          if (end && check > end) return false
          return true
        })
      }
      return true
    })
  }, [visitsWithFollowup, period, selectedStatus, dateFrom, dateTo])

  // Only visits with real coordinates inside India for the map view
  const timelineVisits = useMemo(() => {
    const validTimeline = filteredVisits.filter(visit => {
      if (visit.area_name === 'Lunch Break') return true;
      const lat = parseFloat(visit.latitude)
      const lng = parseFloat(visit.longitude)
      return !isNaN(lat) && !isNaN(lng) && lat >= 8.0 && lat <= 38.0 && lng >= 68.0 && lng <= 98.0
    })

    const getDistance = (lat1, lon1, lat2, lon2) => {
      const R = 6371
      const dLat = (lat2 - lat1) * Math.PI / 180
      const dLon = (lon2 - lon1) * Math.PI / 180
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon/2) * Math.sin(dLon/2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
      return R * c * 1.4 // Apply 1.4x detour factor for urban road distance estimation
    }

    const groups = {}
    validTimeline.forEach(v => {
      const pId = v.person_detail?.id || 'unknown'
      if (!groups[pId]) groups[pId] = []
      groups[pId].push(v)
    })
    
    let processed = []
    Object.keys(groups).forEach(pId => {
      const sorted = groups[pId].sort((a, b) => {
        const timeA = new Date(a.end_time || a.school_arrival_time || a.start_time || a.visited_date || a.created_at).getTime()
        const timeB = new Date(b.end_time || b.school_arrival_time || b.start_time || b.visited_date || b.created_at).getTime()
        return timeA - timeB
      })

      let lastValidSchool = null;

      sorted.forEach((v, idx) => {
        if (v.area_name === 'Lunch Break') {
          processed.push({ ...v, sequence: idx + 1, is_lunch: true, prev_distance_km: 0, travel_time_mins: 0, estimated_travel_mins: 0, is_first: idx === 0, is_last: idx === sorted.length - 1 })
          return
        }

        let prev_distance_km = 0
        let travel_time_mins = 0
        let estimated_travel_mins = 0

        if (lastValidSchool) {
          prev_distance_km = getDistance(parseFloat(lastValidSchool.latitude), parseFloat(lastValidSchool.longitude), parseFloat(v.latitude), parseFloat(v.longitude))
          estimated_travel_mins = Math.round((prev_distance_km / 20) * 60)
          
          const arrivalTime = v.school_arrival_time ? new Date(v.school_arrival_time) : new Date(v.visited_date || v.created_at)
          let travelStart = v.start_time ? new Date(v.start_time) : null
          
          if (!travelStart) {
             travelStart = lastValidSchool.end_time ? new Date(lastValidSchool.end_time) : new Date(lastValidSchool.visited_date || lastValidSchool.created_at)
          }

          if (travelStart) {
            let diff = arrivalTime.getTime() - travelStart.getTime()
            
            const lunchBreaksInBetween = sorted.filter(lb => 
               lb.area_name === 'Lunch Break' && 
               new Date(lb.start_time || lb.created_at).getTime() >= travelStart.getTime() &&
               new Date(lb.end_time || lb.school_arrival_time || lb.created_at).getTime() <= arrivalTime.getTime()
            )

            lunchBreaksInBetween.forEach(lb => {
               const lbStart = new Date(lb.start_time || lb.created_at).getTime()
               const lbEnd = new Date(lb.end_time || lb.school_arrival_time || lb.created_at).getTime()
               if (lbEnd > lbStart) diff -= (lbEnd - lbStart)
            })

            if (diff > 0) travel_time_mins = Math.round(diff / 60000)
          }
        } else {
          const arrivalTime = v.school_arrival_time ? new Date(v.school_arrival_time) : new Date(v.visited_date || v.created_at)
          let travelStart = v.start_time ? new Date(v.start_time) : null
          if (travelStart) {
            let diff = arrivalTime.getTime() - travelStart.getTime()
            if (diff > 0) travel_time_mins = Math.round(diff / 60000)
          }
        }

        processed.push({ ...v, sequence: idx + 1, prev_distance_km, travel_time_mins, estimated_travel_mins, is_first: idx === 0, is_last: idx === sorted.length - 1 })
        lastValidSchool = v
      })
    })

    return processed
  }, [filteredVisits])

  const mappedVisits = useMemo(() => {
    const schools = timelineVisits.filter(v => v.area_name !== 'Lunch Break')
    const seen = {}
    return schools.map(v => {
      const schoolLat = v.school_latitude != null && v.school_latitude !== '' ? v.school_latitude : v.latitude
      const schoolLng = v.school_longitude != null && v.school_longitude !== '' ? v.school_longitude : v.longitude
      let lat = parseFloat(schoolLat)
      let lng = parseFloat(schoolLng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return { ...v, adjusted_lat: lat, adjusted_lng: lng }
      }
      const key = `${lat.toFixed(4)},${lng.toFixed(4)}`
      if (seen[key]) {
        const count = seen[key]
        const angle = count * 1.5
        const radius = 0.00065 * Math.sqrt(count)
        lat += radius * Math.cos(angle)
        lng += radius * Math.sin(angle)
        seen[key] = count + 1
      } else {
        seen[key] = 1
      }
      return { ...v, adjusted_lat: lat, adjusted_lng: lng }
    })
  }, [timelineVisits])

  const personPaths = useMemo(() => {
    const groups = {}
    mappedVisits.forEach(v => {
      const pId = v.person_detail?.id || 'unknown'
      if (!groups[pId]) groups[pId] = []
      groups[pId].push(v)
    })
    
    const paths = []
    const colors = ['#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#db2777', '#0891b2']
    let colorIdx = 0
    
    Object.keys(groups).forEach(pId => {
      if (groups[pId].length > 1) {
        groups[pId].sort((a, b) => {
          const dateA = new Date(a.visited_date || a.created_at || a.planned_date).getTime()
          const dateB = new Date(b.visited_date || b.created_at || b.planned_date).getTime()
          return dateA - dateB
        })
        paths.push({
          person: groups[pId][0].person_detail?.name || 'Unknown',
          color: colors[colorIdx % colors.length],
          positions: groups[pId]
            .map(v => [v.adjusted_lat, v.adjusted_lng])
            .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng))
        })
        colorIdx++
      }
    })
    return paths
  }, [mappedVisits])

  const mapCenter = useMemo(() => {
    const placed = mappedVisits.find((visit) => Number.isFinite(visit.adjusted_lat) && Number.isFinite(visit.adjusted_lng))
      || mappedVisits.find((visit) => Number.isFinite(parseFloat(visit.end_latitude)) && Number.isFinite(parseFloat(visit.end_longitude)))
    if (placed?.adjusted_lat != null && Number.isFinite(placed.adjusted_lat)) {
      return [placed.adjusted_lat, placed.adjusted_lng]
    }
    if (placed) return [parseFloat(placed.end_latitude), parseFloat(placed.end_longitude)]
    return [13.0953, 77.3964]
  }, [mappedVisits])

  // Filtered list of specifically visited companies for the summary tab
  const visitedCompaniesSummary = useMemo(() => {
    return filteredVisits.filter(v => v.status === 'visited')
  }, [filteredVisits])

  useEffect(() => {
    setSummaryPage(1)
  }, [visitedCompaniesSummary.length, period, dateFrom, dateTo, selectedPerson, selectedStatus])

  const paginatedSummary = useMemo(() => {
    const start = (summaryPage - 1) * summaryItemsPerPage
    return visitedCompaniesSummary.slice(start, start + summaryItemsPerPage)
  }, [visitedCompaniesSummary, summaryPage, summaryItemsPerPage])

  const totalSummaryPages = Math.ceil(visitedCompaniesSummary.length / summaryItemsPerPage)

  // Handle company search select
  const handleCompanySelect = (name) => {
    setSelectedCompany(name)
    if (activeTab !== 'company') {
      setPreviousTab(activeTab)
    }
    setActiveTab('company')
  }

  const handleCompanyClear = () => {
    setSelectedCompany('')
    if (activeTab === 'company') setActiveTab(previousTab)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', height: '100%' }}>
      <div className="page-header" style={{ paddingBottom: '10px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Visits Tracking</h1>
          <p className="page-subtitle">Track salesperson visits, school interest levels and follow-up schedules</p>
        </div>

        {/* Tab Selector */}
        <div style={{ display: 'flex', gap: '8px', background: '#f1f5f9', padding: '4px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
          <button
            className={`btn ${activeTab === 'map' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            onClick={() => setActiveTab('map')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', border: 'none', boxShadow: activeTab === 'map' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', padding: '6px 14px' }}
          >
            🗺️ Map View
          </button>
          {isAdmin && (
            <button
              className={`btn ${activeTab === 'summary' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              onClick={() => setActiveTab('summary')}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', border: 'none', boxShadow: activeTab === 'summary' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', padding: '6px 14px' }}
            >
              📋 Visitors Summary
            </button>
          )}
          {selectedCompany && (
            <button
              className={`btn ${activeTab === 'company' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              onClick={() => setActiveTab('company')}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', border: 'none', boxShadow: activeTab === 'company' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', padding: '6px 14px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={selectedCompany}
            >
              🏫 {selectedCompany.length > 20 ? selectedCompany.slice(0, 20) + '…' : selectedCompany}
              <span
                onMouseDown={e => { e.stopPropagation(); handleCompanyClear() }}
                style={{ marginLeft: '4px', display: 'flex', alignItems: 'center', opacity: 0.7 }}
                title="Close"
              >
                <X size={13} />
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Filters Toolbar */}
      <div className="card" style={{ padding: '14px 20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>

          {/* Company Search */}
          <CompanySearchBox
            visits={visitsWithFollowup}
            onSelect={handleCompanySelect}
            onClear={handleCompanyClear}
            selectedCompany={selectedCompany}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>Year:</span>
            <select
              className="form-control"
              value={selectedAcademicYear}
              onChange={e => setSelectedAcademicYear(e.target.value)}
              style={{ fontSize: '13px', padding: '5px 10px', height: '34px' }}
            >
              {academicYears.length === 0 && <option value="">Loading...</option>}
              {academicYears.map(year => (
                <option key={year.id} value={String(year.id)}>{year.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <User size={14} /> Staff:
            </span>
            <select className="form-control" value={selectedPerson} onChange={e => setSelectedPerson(e.target.value)} style={{ fontSize: '13px', padding: '5px 10px', height: '34px' }}>
              <option value="">All Staff</option>
              {persons.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>Status:</span>
            <select className="form-control" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} style={{ fontSize: '13px', padding: '5px 10px', height: '34px' }} disabled={activeTab === 'summary'}>
              <option value="visited">Visited</option>
              <option value="planned">Planned</option>
              <option value="">All</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Calendar size={14} /> Date range:
            </span>
            <div style={{ display: 'flex', alignItems: 'center', background: '#f1f5f9', padding: '3px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <button onClick={() => { setPeriod('all'); setDateFrom(''); setDateTo(''); }} style={{
                border: 'none', background: period === 'all' ? '#fff' : 'transparent',
                color: period === 'all' ? 'var(--accent-blue)' : 'var(--text-secondary)',
                fontWeight: 600, fontSize: '12px', padding: '5px 14px', borderRadius: '6px',
                cursor: 'pointer', boxShadow: period === 'all' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.2s'
              }}>All</button>

              <input
                type="date"
                title="From date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={e => {
                  setDateFrom(e.target.value)
                  setPeriod(e.target.value || dateTo ? 'custom' : 'all')
                }}
                style={{
                  border: 'none', background: period === 'custom' ? '#fff' : 'transparent',
                  color: period === 'custom' ? 'var(--accent-blue)' : 'var(--text-secondary)',
                  fontWeight: 600, fontSize: '12px', padding: '4px 8px', borderRadius: '6px',
                  cursor: 'pointer', outline: 'none', margin: '0 4px',
                  boxShadow: period === 'custom' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  fontFamily: 'inherit'
                }}
              />
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>to</span>
              <input
                type="date"
                title="To date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={e => {
                  setDateTo(e.target.value)
                  setPeriod(dateFrom || e.target.value ? 'custom' : 'all')
                }}
                style={{
                  border: 'none', background: period === 'custom' ? '#fff' : 'transparent',
                  color: period === 'custom' ? 'var(--accent-blue)' : 'var(--text-secondary)',
                  fontWeight: 600, fontSize: '12px', padding: '4px 8px', borderRadius: '6px',
                  cursor: 'pointer', outline: 'none', margin: '0 4px',
                  boxShadow: period === 'custom' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  fontFamily: 'inherit'
                }}
              />

              {['Week', 'Month', 'Year'].map(label => {
                const id = label.toLowerCase()
                return (
                  <button key={id} onClick={() => { setPeriod(id); setDateFrom(''); setDateTo(''); }} style={{
                    border: 'none', background: period === id ? '#fff' : 'transparent',
                    color: period === id ? 'var(--accent-blue)' : 'var(--text-secondary)',
                    fontWeight: 600, fontSize: '12px', padding: '5px 14px', borderRadius: '6px',
                    cursor: 'pointer', boxShadow: period === id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    transition: 'all 0.2s'
                  }}>{label}</button>
                )
              })}
            </div>
          </div>

          <span style={{ marginLeft: 'auto', fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>
            {activeTab === 'company' && selectedCompany
              ? `🏫 Showing: ${selectedCompany}`
              : (!isAdmin || activeTab === 'map')
                ? `📍 ${mappedVisits.length} locations shown`
                : `📋 ${visitedCompaniesSummary.length} visited companies`}
          </span>
        </div>
      </div>

      {/* ── Tab Content ── */}
      {activeTab === 'map' ? (
        /* 🗺️ MAP VIEW TAB */
        <div className="search-layout" style={{ gridTemplateColumns: '1fr 300px' }}>
          {/* Map */}
          <div className="card" style={{ height: '600px', padding: 0, overflow: 'hidden', position: 'relative' }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <div className="spinner" />&nbsp;<span>Loading visits...</span>
              </div>
            ) : mappedVisits.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: '12px', color: 'var(--text-muted)' }}>
                <MapPin size={48} />
                <strong>No captured locations found</strong>
                <span style={{ fontSize: '13px', textAlign: 'center', maxWidth: '280px' }}>
                  Use the <strong>Capture</strong> button on a visit to take a photo — GPS will be saved automatically and the company will appear here.
                </span>
              </div>
            ) : (
              <GoogleVisitsMap
                visits={mappedVisits}
                paths={personPaths}
                center={mapCenter}
                onCompany={handleCompanySelect}
              />
            )}
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Legend */}
            <div className="card" style={{ padding: '18px' }}>
              <h3 className="card-title" style={{ fontSize: '14px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Info size={15} /> Legend
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  ['#10b981', 'Interested'],
                  ['#ef4444', 'Not Interested'],
                  ['#64748b', 'Pending / Unknown'],
                ].map(([color, label]) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: 12, height: 12, borderRadius: '50%', background: color, border: '1.5px solid #fff', boxShadow: '0 0 3px rgba(0,0,0,0.2)', flexShrink: 0 }} />
                    <span style={{ fontSize: '13px' }}>{label}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#f59e0b', border: '1.5px solid #fff', boxShadow: '0 0 3px rgba(0,0,0,0.2)', flexShrink: 0 }} />
                  <span style={{ fontSize: '13px' }}>Ended point</span>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
                  Numbered pins are the school. Orange pins are where the visit ended. Support visits are included.
                </div>
              </div>
            </div>

            {/* Summary / Timeline */}
            {selectedPerson ? (
              <div className="card" style={{ padding: '18px', maxHeight: '420px', overflowY: 'auto' }}>
                <h3 className="card-title" style={{ fontSize: '14px', marginBottom: '12px' }}>📍 Route Timeline</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {timelineVisits.sort((a,b) => a.sequence - b.sequence).map(v => (
                    <div key={v.id} style={{ display: 'flex', gap: '10px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: v.is_lunch ? '#f59e0b' : '#3b82f6', color: '#fff', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {v.is_lunch ? '🥪' : v.sequence}
                        </div>
                        {!v.is_last && <div style={{ width: 2, flex: 1, background: '#e2e8f0', margin: '4px 0' }} />}
                      </div>
                      <div style={{ flex: 1, paddingBottom: v.is_last ? 0 : '10px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{v.is_lunch ? 'Lunch Break' : schoolName(v.area_name)}</div>
                        
                        {v.is_lunch ? (
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                            {formatTime(v.start_time || v.created_at)} - {(v.end_time || v.school_arrival_time) ? formatTime(v.end_time || v.school_arrival_time) : 'In Progress'}
                          </div>
                        ) : (
                          <>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Arrived: {formatTime(v.school_arrival_time)}</div>
                            {(() => {
                              const diff = (v.travel_time_mins || 0) - (v.estimated_travel_mins || 0)
                              let bg = 'transparent'
                              let color = '#64748b'
                              if (diff >= 30) { bg = '#fee2e2'; color = '#991b1b' }
                              else if (diff >= 15) { bg = '#ffedd5'; color = '#9a3412' }
                              return (
                               <div style={{ fontSize: '11px', color, marginTop: '4px', padding: '4px 6px', borderRadius: '4px', background: bg }}>
                                 {v.prev_distance_km.toFixed(2)} km | Est: {v.estimated_travel_mins > 0 ? v.estimated_travel_mins + 'm' : '< 1m'} | Actual: {v.travel_time_mins > 0 ? v.travel_time_mins + 'm' : '—'}
                               </div>
                              )
                            })()}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="card" style={{ padding: '18px' }}>
                <h3 className="card-title" style={{ fontSize: '14px', marginBottom: '12px' }}>📊 Summary</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Shown on map:</span>
                    <strong style={{ color: '#2563eb' }}>{mappedVisits.length}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Interested:</span>
                    <strong style={{ color: '#10b981' }}>{mappedVisits.filter(v => v.interest_status === 'interested').length}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Not Interested:</span>
                    <strong style={{ color: '#ef4444' }}>{mappedVisits.filter(v => v.interest_status === 'not_interested').length}</strong>
                  </div>

                  {mappedVisits.length > 0 && (
                    <>
                      <div style={{ margin: '4px 0', borderTop: '1px solid var(--border-color)' }}></div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '2px' }}>Visits by Staff:</div>
                      {Object.entries(mappedVisits.reduce((acc, v) => {
                        const name = v.person_detail?.name || 'Unknown'
                        acc[name] = (acc[name] || 0) + 1
                        return acc
                      }, {})).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                        <div key={name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                          <span style={{ color: 'var(--text-muted)' }}>{name}</span>
                          <strong style={{ color: 'var(--text-primary)' }}>{count}</strong>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Tip card */}
            <div className="card" style={{ padding: '14px 16px', background: 'linear-gradient(135deg,#eff6ff,#f0fdf4)', border: '1px solid #bfdbfe' }}>
              <div style={{ fontSize: '12px', color: '#1e40af', fontWeight: 600, marginBottom: '6px' }}>💡 Tip</div>
              <div style={{ fontSize: '12px', color: '#334155', lineHeight: '1.6' }}>
                Search a company above or click <strong>"View Visit Summary"</strong> on any map marker to open its detailed visit report in a new tab.
              </div>
            </div>
          </div>
        </div>

      ) : activeTab === 'summary' ? (
        /* 📋 VISITORS SUMMARY TAB */
        <div className="card" style={{ padding: '20px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <div className="spinner" />&nbsp;<span>Loading summary details...</span>
            </div>
          ) : visitedCompaniesSummary.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '200px', gap: '10px', color: 'var(--text-muted)' }}>
              <CheckCircle size={32} />
              <span>No visited companies found for the selected filters.</span>
            </div>
          ) : (
            <div className="table-container">
              <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>🏫 Visited Company</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>👤 Visitor Name</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>Interest Status</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>📞 Contact Number</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>📅 Planned Date</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>📅 Visited Date</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>📞 Next Follow-up Date</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>Purpose of Next Visit</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSummary.map(visit => {
                    const statusColors = {
                      interested: { bg: '#dcfce7', text: '#16a34a' },
                      not_interested: { bg: '#fee2e2', text: '#ef4444' },
                      pending: { bg: '#f1f5f9', text: '#64748b' }
                    }
                    const badge = statusColors[visit.interest_status] || statusColors.pending
                    const followupDate = visit.latest_followup?.followup_date || visit.revisit_date

                    return (
                      <tr key={visit.id} style={{ borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}
                        onClick={() => handleCompanySelect(schoolName(visit.area_name))}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                        title="Click to view company visit summary"
                      >
                        <td style={{ padding: '14px 16px', fontWeight: 600, fontSize: '13px', color: '#2563eb' }}>
                          {schoolName(visit.area_name)}
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--text-primary)' }}>
                          <strong>{visit.person_detail?.name || '—'}</strong>
                          {visit.is_support_visit && (
                            <div style={{ fontSize: '11px', color: '#7c3aed', fontWeight: 700, marginTop: '2px' }}>
                              Support visit
                            </div>
                          )}
                          {visit.person_detail?.designation && visit.person_detail.designation !== 'Support' && (
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                              {visit.person_detail.designation}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700,
                            background: badge.bg, color: badge.text
                          }}>
                            {visit.interest_status === 'interested' ? 'Interested' : visit.interest_status === 'not_interested' ? 'Not Interested' : 'Pending'}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--text-primary)' }}>
                          {visit.interest_status === 'interested' && visit.contact_number ? (
                            <strong>📞 {visit.contact_number}</strong>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--text-muted)' }}>
                          {visit.planned_date ? new Date(visit.planned_date).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--text-muted)' }}>
                          {visit.visited_date ? new Date(visit.visited_date).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: 600, color: followupDate ? 'var(--accent-blue)' : 'var(--text-muted)' }}>
                          {followupDate ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <Clock size={13} />
                              {new Date(followupDate).toLocaleDateString()}
                            </span>
                          ) : 'No follow-up'}
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={visit.latest_followup?.next_purpose || '—'}>
                          {visit.latest_followup?.next_purpose || '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          {visitedCompaniesSummary.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '16px', gap: '24px', borderTop: '1px solid var(--border-color)', marginTop: '10px', color: 'var(--text-primary)', fontSize: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Rows per page:</span>
                <select
                  value={summaryItemsPerPage}
                  onChange={(e) => { setSummaryItemsPerPage(Number(e.target.value)); setSummaryPage(1); }}
                  style={{ border: 'none', background: 'transparent', fontSize: '14px', outline: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <span style={{ color: 'var(--text-secondary)' }}>
                {Math.min((summaryPage - 1) * summaryItemsPerPage + 1, visitedCompaniesSummary.length)}–{Math.min(summaryPage * summaryItemsPerPage, visitedCompaniesSummary.length)} of {visitedCompaniesSummary.length}
              </span>
              <div style={{ display: 'flex', gap: '16px' }}>
                <button
                  style={{ border: 'none', background: 'transparent', cursor: summaryPage === 1 ? 'default' : 'pointer', color: summaryPage === 1 ? '#cbd5e1' : '#64748b', padding: 0, display: 'flex', alignItems: 'center' }}
                  disabled={summaryPage === 1}
                  onClick={() => setSummaryPage(prev => Math.max(1, prev - 1))}
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  style={{ border: 'none', background: 'transparent', cursor: summaryPage === totalSummaryPages || totalSummaryPages === 0 ? 'default' : 'pointer', color: summaryPage === totalSummaryPages || totalSummaryPages === 0 ? '#cbd5e1' : '#64748b', padding: 0, display: 'flex', alignItems: 'center' }}
                  disabled={summaryPage === totalSummaryPages || totalSummaryPages === 0}
                  onClick={() => setSummaryPage(prev => Math.min(totalSummaryPages, prev + 1))}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}
        </div>

      ) : (
        /* 🏫 COMPANY DETAIL TAB */
        <div className="card" style={{ padding: '24px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <div className="spinner" />&nbsp;<span>Loading company details...</span>
            </div>
          ) : (
            <CompanyDetailTab
              company={selectedCompany}
              allVisits={visitsWithFollowup}
              followups={followups}
            />
          )}
        </div>
      )}
    </div>
  )
}
